import { useState, useEffect, useMemo, useCallback } from "react";

export type MediaPermissionStatus = "notEnabled" | "permanentlyBlocked";

interface BlockedDeviceFlags {
  cameraBlocked: boolean;
  micBlocked: boolean;
}

interface UsePermissionsReturn {
  hasPermissions: boolean;
  showPermissionNote: boolean;
  permissionStatus: MediaPermissionStatus;
  permissionRequestAttempts: number;
  requestPermissions: () => Promise<void>;
  setShowPermissionNote: (show: boolean) => void;
  cameraBlocked: boolean;
  micBlocked: boolean;
  windowManagementBlocked: boolean;
  windowManagementState: PermissionState | "unsupported";
}

export const usePermissions = (): UsePermissionsReturn => {
  const [devicePermissionsGranted, setDevicePermissionsGranted] = useState(false);
  const [showPermissionNote, setShowPermissionNote] = useState(false);
  const [permissionStatus, setPermissionStatus] =
    useState<MediaPermissionStatus>("notEnabled");
  const [permissionRequestAttempts, setPermissionRequestAttempts] = useState(0);

  // Track which specific permissions are blocked
  const [blockedDevices, setBlockedDevices] = useState<BlockedDeviceFlags>({
    cameraBlocked: false,
    micBlocked: false,
  });

  const [windowManagementState, setWindowManagementState] = useState<
    PermissionState | "unsupported"
  >("unsupported");

  const windowManagementBlocked = useMemo(() => {
    if (windowManagementState === "unsupported") {
      return false;
    }
    return windowManagementState !== "granted";
  }, [windowManagementState]);

  const hasPermissions = useMemo(() => {
    return devicePermissionsGranted && !windowManagementBlocked;
  }, [devicePermissionsGranted, windowManagementBlocked]);

  const updateWindowManagementPermission = useCallback(async () => {
    // First try the getScreenDetails API approach
    if ("getScreenDetails" in window) {
      try {
        await (window as any).getScreenDetails();
        setWindowManagementState("granted");
        return;
      } catch (error: any) {
        if (
          error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError"
        ) {
          setWindowManagementState("denied");
          setShowPermissionNote(true);
          return;
        }
        // If it's not a permission error, fall through to Permissions API
      }
    }

    // Fallback to Permissions API
    const permissionsApi = (navigator as any)?.permissions;

    if (!permissionsApi || typeof permissionsApi.query !== "function") {
      setWindowManagementState("unsupported");
      return;
    }

    try {
      const status: globalThis.PermissionStatus = await permissionsApi.query({
        /*
         * Typescript's lib.dom definitions don't yet include "window-management".
         * Cast to keep the browser call typed without widening the entire API surface.
         */
        name: "window-management" as PermissionName,
      });

      const nextState = status.state;
      setWindowManagementState(nextState);

      // The useEffect watching hasPermissions will automatically close the modal
      // when all permissions are granted, so we just need to update state here
      if (nextState !== "granted") {
        setShowPermissionNote(true);
      }

      status.onchange = () => {
        const next = status.state;
        setWindowManagementState(next);
        if (next !== "granted") {
          setShowPermissionNote(true);
        }
      };
    } catch (err) {
      // Permission query unsupported or blocked by browser policies – treat as unsupported.
      setWindowManagementState("unsupported");
    }
  }, [setShowPermissionNote]);

  const requestPermissions = async () => {
    try {
      // Check if only window management permission is blocked (cannot be requested programmatically)
      const onlyWindowManagementBlocked =
        windowManagementBlocked &&
        !blockedDevices.cameraBlocked &&
        !blockedDevices.micBlocked;

      if (onlyWindowManagementBlocked) {
        // Try to trigger window management permission request by using getScreenDetails
        try {
          // Try to use getScreenDetails API which should prompt for window management permission
          if ("getScreenDetails" in window) {
            await (window as any).getScreenDetails();

            // Update state to reflect granted permission
            setWindowManagementState("granted");
            setShowPermissionNote(false);

            // Refresh the page to properly initialize with the new permission
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          } else {
            // No support for getScreenDetails
          }
        } catch (error) {
          // Check if it's a permission denied error
          if (
            (error as any)?.name === "NotAllowedError" ||
            (error as any)?.name === "PermissionDeniedError"
          ) {
            // Keep modal open - user needs to enable it in browser settings
            return;
          }
        }

        // Fallback: re-check permission status
        const permissionsApi = (navigator as any)?.permissions;
        if (permissionsApi && typeof permissionsApi.query === "function") {
          try {
            const status: globalThis.PermissionStatus = await permissionsApi.query({
              name: "window-management" as PermissionName,
            });

            // Update state with latest status
            const currentState = status.state;
            setWindowManagementState(currentState);

            // If permission is now granted, close the modal and refresh
            if (currentState === "granted") {
              setShowPermissionNote(false);

              // Refresh the page to properly initialize with the new permission
              setTimeout(() => {
                window.location.reload();
              }, 500);
              return;
            }
          } catch (err) {
            // ignore permission query failures
          }
        }

        // Otherwise, keep modal open - user needs to enable it in browser settings
        return;
      }

      // Handle camera/mic permissions
      setPermissionRequestAttempts((prev) => prev + 1);

      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        // Stop the stream immediately as we just needed to activate permissions
        stream.getTracks().forEach((track) => track.stop());

        setDevicePermissionsGranted(true);
        setBlockedDevices({ cameraBlocked: false, micBlocked: false });
        setPermissionStatus("notEnabled"); // Reset status

        // Re-evaluate window management permission in case the browser updated state.
        await updateWindowManagementPermission();

        // Give a brief moment for state to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if window management is still blocked after camera/mic grant
        // Re-check the actual permission status
        const permissionsApi = (navigator as any)?.permissions;
        let windowManagementGranted = true;

        if (permissionsApi && typeof permissionsApi.query === "function") {
          try {
            const status: globalThis.PermissionStatus = await permissionsApi.query({
              name: "window-management" as PermissionName,
            });
            windowManagementGranted = status.state === "granted";
            setWindowManagementState(status.state);
          } catch (err) {
            // If query fails, assume unsupported (not blocked)
            windowManagementGranted = true;
          }
        }

        if (!windowManagementGranted) {
          // Window management still needed - don't refresh, keep modal open
          setShowPermissionNote(true);
          return;
        }

        // All permissions granted
        setShowPermissionNote(false);

        // Refresh the page to properly initialize media devices
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        throw new Error("getUserMedia not supported in this browser.");
      }
    } catch (error) {
      setDevicePermissionsGranted(false);
      setShowPermissionNote(true);

      // If we've tried more than once, assume it's permanently blocked
      if (permissionRequestAttempts >= 1) {
        setPermissionStatus("permanentlyBlocked");
      }
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        // Check window management permission FIRST since it's critical
        await updateWindowManagementPermission();

        // Check device status to see if permissions are already granted
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(
          (device) => device.kind === "videoinput" && device.label !== "",
        );
        const hasMic = devices.some(
          (device) => device.kind === "audioinput" && device.label !== "",
        );

        if (hasCamera && hasMic) {
          setDevicePermissionsGranted(true);
          setBlockedDevices({ cameraBlocked: false, micBlocked: false });
          // Don't set showPermissionNote here - let hasPermissions useEffect handle it
          // This ensures window management check can still show modal if needed
        } else {
          setDevicePermissionsGranted(false);
          setShowPermissionNote(true);

          // Check if devices exist but don't have labels (likely means they're blocked)
          const cameraExists = devices.some(
            (device) => device.kind === "videoinput",
          );
          const micExists = devices.some(
            (device) => device.kind === "audioinput",
          );

          // Update blocked devices flags
          setBlockedDevices({
            cameraBlocked: cameraExists && !hasCamera,
            micBlocked: micExists && !hasMic,
          });

          if ((cameraExists && !hasCamera) || (micExists && !hasMic)) {
            // If we've already tried requesting permissions, mark as permanently blocked
            if (permissionRequestAttempts > 0) {
              setPermissionStatus("permanentlyBlocked");
            }
          }
        }
      } catch (err) {
        setDevicePermissionsGranted(false);
        setShowPermissionNote(true);

        // If we get a permissions error, consider it permanently blocked
        if (
          (err as any)?.name === "NotAllowedError" ||
          (err as any)?.name === "PermissionDeniedError"
        ) {
          setPermissionStatus("permanentlyBlocked");
          setBlockedDevices({ cameraBlocked: true, micBlocked: true });
        }
      }
    };

    checkPermissions();
  }, [permissionRequestAttempts, updateWindowManagementPermission]);

  useEffect(() => {
    if (hasPermissions) {
      setShowPermissionNote(false);
    }
  }, [hasPermissions, setShowPermissionNote]);

  return {
    hasPermissions,
    showPermissionNote,
    permissionStatus,
    permissionRequestAttempts,
    requestPermissions,
    setShowPermissionNote,
    cameraBlocked: blockedDevices.cameraBlocked,
    micBlocked: blockedDevices.micBlocked,
    windowManagementBlocked,
    windowManagementState,
  };
};

